import JSZip from "jszip";
import { createDownloadLink } from "./createDownloadLink";
import { getUniqueZipFilePath } from "../zipUniqueFilename";

interface CreatePdfZipBundleParams {
  pdfBlob: Blob;
  pdfFilename: string;
  zipFilename: string;
  files: { id: string; filename_download?: string; extension?: string }[];
  fetchBlob: (fileId: string) => Promise<Blob>;
}

/**
 * Downloads a PDF directly when there are no extra files,
 * otherwise bundles the PDF + fetched files into a ZIP.
 */
export const createPdfZipBundle = async ({
  pdfBlob,
  pdfFilename,
  zipFilename,
  files,
  fetchBlob,
}: CreatePdfZipBundleParams): Promise<void> => {
  if (files.length === 0) {
    createDownloadLink({ blob: pdfBlob, filename: pdfFilename });
    return;
  }

  try {
    const zip = new JSZip();
    zip.file(pdfFilename, pdfBlob);

    const usedPaths = new Set<string>();
    const uniquePaths = files.map((file) => {
      const extension = file.extension || "file";
      const name = String(file.filename_download || `file-${file.id}.${extension}`);
      return getUniqueZipFilePath(name, usedPaths);
    });

    const filePromises = files.map(async (file, i) => {
      try {
        const fileBlob = await fetchBlob(file.id);
        zip.file(uniquePaths[i], fileBlob);
      } catch (error) {
        console.error(`Error downloading file ${file.id}:`, error);
      }
    });

    await Promise.all(filePromises);

    const zipBlob = await zip.generateAsync({ type: "blob" });
    createDownloadLink({ blob: zipBlob, filename: zipFilename });
  } catch (error) {
    console.error("Error creating ZIP:", error);
  }
};
